import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {ConfigState} from '../../../store/config';
import {createIntl, createIntlCache, IntlCache} from 'react-intl';
import {IntlShape} from 'react-intl/src/types';
import {MonitorConfig, VideoMonitor} from './VideoStreamMonitor';
import {VideoServerClient} from './VideoServerClient';
import {HlsClient} from './HlsClient';
import messages from '../../en.json';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from './VideoStreamSubject';
import {Parameter} from '../../../types/model/framework/Parameter';

export type VideoStreamModel = 'PUSH' | 'PULL' | 'PROXIED_PUSH';

export type VideoStreamConfig = {
  source?: InputSource | '';
  url?: string;
  sourceUrl?: string;
  callerSession?: string;
  targetSession?: string;
  serverConfig: ConfigState;
  streamModel: VideoStreamModel;
  projectId: string;
  limitedDuration?: boolean;
  monitorConfig: MonitorConfig;
  internalSource?: boolean;
  internalNetwork?: boolean;
  streamSubject: VideoStreamSubject;
};

class ControllerObserver extends VideoStreamObserver {
  controller: VideoStreamController;
  config: VideoStreamConfig;

  constructor(controller: VideoStreamController, config: VideoStreamConfig) {
    super();
    this.controller = controller;
    this.config = config;
  }

  handleEvent(event: VideoStreamEvent) {
    if (event.type === EVENT_TYPE.ERROR || event.type === EVENT_TYPE.STREAM_FINISHED) {
      this.controller.stop();
    }
    event.monitorConfig = this.config.monitorConfig;
  }
}

export class VideoStreamController {
  cache: IntlCache = createIntlCache();
  intl: IntlShape = createIntl(
    {locale: navigator.language, messages: messages},
    this.cache
  );
  monitor: VideoMonitor = new VideoMonitor();
  hlsClient: HlsClient = new HlsClient(this.intl, this.monitor);
  videoServerClient: VideoServerClient = new VideoServerClient(this.intl);

  setVideoUnavailableMessageKey(key: string) {
    this.hlsClient.videoUnavailableMessageKey = key;
  }

  stop(): void {
    this.monitor.interrupted = true;
    this.monitor.stopMonitoring();
    this.hlsClient.interrupted = true;
    this.hlsClient.closeConnection();
    this.videoServerClient.closeConnection();
  }

  async start(config: VideoStreamConfig) {
    this.monitor.interrupted = false;
    this.hlsClient.interrupted = false;
    let videoServerUrl = config.serverConfig.videoServerUrl
      ? config.serverConfig.videoServerUrl
      : '';
    if (config.callerSession) {
      videoServerUrl += `?sessionId=${config.callerSession}`;
    }
    let streamUrl = config.url ? config.url : '';
    config.streamSubject.addListener(
      'ControllerObserver',
      new ControllerObserver(this, config)
    );

    try {
      config.streamSubject.raiseConnectingEvent();
      await this.videoServerClient.openConnection(videoServerUrl);
      this.videoServerClient.addHlsWebSocketListeners(config.streamSubject, () =>
        this.createHlsConnection(streamUrl, config)
      );
      this.videoServerClient.registerHlsConnection(config);
    } catch (e) {
      this.processConnectionError(config, e);
    }
  }

  private createHlsConnection(streamUrl: string, config: VideoStreamConfig) {
    try {
      this.monitor.init(config.monitorConfig, msg =>
        this.videoServerClient.sendStatistics(msg)
      );
      this.hlsClient.openConnection(streamUrl, config.streamSubject);
      this.monitor.startMonitoring();
    } catch (e) {
      this.processConnectionError(config, e);
    }
  }

  private processConnectionError(config: VideoStreamConfig, e: any) {
    console.error('Error starting HLS stream.', e);
    this.monitor.stopMonitoring();
    config.streamSubject.raiseInitErrorEvent(undefined);
  }

  async updateParameters(parameters: Array<Parameter>) {
    this.videoServerClient.sendParameters(parameters);
  }
}
